import {
  getApiClient,
  HttpError,
  type ApiFileUploadResult,
} from '@littlebird/sdk';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { logError } from '@/lib/errorLogging';

import {
  ChatGptExportTooLargeError,
  extractChatGptExport,
  NotChatGptExportError,
} from './chatgpt-export-zip';

export type ChatImportResult = {
  imported: number;
  duplicates: number;
  failed: number;
  empty: number;
  messages_imported: number;
  markup_dropped: number;
};

export type ChatGptImportStatus = {
  status: 'idle' | 'running' | 'completed' | 'failed';
  phase: 'importing' | 'done' | null;
  result: ChatImportResult | null;
};

export const CHATGPT_IMPORT_MUTATION_KEY = ['import-chatgpt-export'];
const STATUS_QUERY_KEY = ['chatgpt-import', 'status'];
const STATUS_POLL_MS = 5000;
const RUNNING_STATUS: ChatGptImportStatus = {
  status: 'running',
  phase: 'importing',
  result: null,
};

const getChatGptImportStatus = () =>
  getApiClient().fetch<ChatGptImportStatus>('chat_import/chatgpt/status');

const startChatGptImport = async (
  fileId: string
): Promise<ChatGptImportStatus> => {
  try {
    return await getApiClient().fetch<ChatGptImportStatus>(
      `chat_import/chatgpt/${fileId}`,
      { method: 'POST' }
    );
  } catch (error) {
    // The backend answers 409 when an import is already running
    if (error instanceof HttpError && error.status === 409) {
      return RUNNING_STATUS;
    }
    throw error;
  }
};

const getUploadUrl = async (fileId: string, file: File) => {
  const { url } = await getApiClient().fetch<ApiFileUploadResult>(
    'get_upload_url',
    {
      method: 'POST',
      body: JSON.stringify({
        file_metadata: {
          id: fileId,
          filename: file.name,
          content_type: file.type,
          size: file.size,
          entity: 'chatgpt_import',
        },
      }),
    }
  );
  return url;
};

const getImportErrorDescription = (error: unknown) => {
  if (error instanceof NotChatGptExportError) {
    return 'This doesn’t look like a ChatGPT export. Choose the .zip file ChatGPT emailed you.';
  }
  if (error instanceof ChatGptExportTooLargeError) {
    return 'This export is too large to import.';
  }
  if (error instanceof HttpError && error.userErrorMessage) {
    return error.userErrorMessage;
  }
  return 'Something went wrong. Please try again.';
};

export const formatImportSummary = (result: ChatImportResult | null) => {
  if (!result) return 'No chats were imported.';
  const { imported, duplicates, failed } = result;

  if (imported === 0 && duplicates > 0 && failed === 0) {
    return 'Everything in this export is already in Littlebird.';
  }

  const parts = [
    {
      count: imported,
      label: imported === 1 ? 'chat imported' : 'chats imported',
    },
    { count: duplicates, label: 'already in Littlebird' },
    { count: failed, label: 'couldn’t be imported' },
  ]
    .filter((part) => part.count > 0)
    .map((part) => `${part.count.toLocaleString()} ${part.label}`);

  return parts.length > 0 ? parts.join(' · ') : 'No chats were imported.';
};

export const useChatGptImportStatusQuery = ({
  enabled = true,
  poll = false,
}: { enabled?: boolean; poll?: boolean } = {}) =>
  useQuery({
    queryKey: STATUS_QUERY_KEY,
    queryFn: getChatGptImportStatus,
    refetchInterval: poll
      ? (query) =>
          query.state.data?.status === 'running' ? STATUS_POLL_MS : false
      : false,
    refetchOnWindowFocus: false,
    enabled,
  });

export const useChatGptImportMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CHATGPT_IMPORT_MUTATION_KEY,
    mutationFn: async (file: File) => {
      const slimFile = await extractChatGptExport(file);
      const apiClient = getApiClient();
      const fileId = crypto.randomUUID();
      const uploadUrl = await getUploadUrl(fileId, slimFile);
      await apiClient.uploadFileWithProgress(uploadUrl, slimFile, () => {})
        .promise;
      await apiClient.fetch(`finalize_upload/${fileId}`, { method: 'POST' });
      return startChatGptImport(fileId);
    },
    onSuccess: async (status) => {
      // A status fetch still in flight could resolve with a stale idle and
      // stop the polling that follows from the running status set here
      await queryClient.cancelQueries({ queryKey: STATUS_QUERY_KEY });
      queryClient.setQueryData(STATUS_QUERY_KEY, status);
    },
    onError: (error) => {
      logError(error, 'Failed to import ChatGPT export', {
        context: 'useChatGptImportMutation',
      });
      toast.error('Couldn’t import your ChatGPT export', {
        description: getImportErrorDescription(error),
      });
    },
  });
};
